import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {BaseFormGroup} from '../../../shared/utils/base-form-group';
import {ContactPerson} from '../../../../api/model/ContactPerson.model';
import {Contact} from '../../../../api/model/Contact.model';
import {ConfigService} from '../../../../core/services';
import {ContactType} from '../../../shared/enums/contact.type';
import {ContactValidationService} from '../../../shared/services/contact-validation.service';

@Component({
  selector: 'tj-contact-persons-form',
  templateUrl: './contact-persons-form.component.html',
  styleUrls: ['./contact-persons-form.component.scss'],
})
export class ContactPersonsFormComponent extends BaseFormGroup implements OnInit, OnChanges {
  @Input() contactPersons: ContactPerson[] = [];

  @Input() mainContactPerson: ContactPerson;

  @Input() requiredOne = true;

  @Input() form: UntypedFormGroup;

  @Input() group: string;

  @Input() canChangeMainContactPerson = true;

  @Input() receivedByDefaultEnabled = false;

  @Input() receiveNotificationsRequestedRep = false;

  @Input() notificationReceiver = false;

  @Input() invoiceReceiver = false;

  @Input() pdfReceiver = false;

  @Input() receiveInvoiceRequestedRep = false;

  @Input() receivesComments = false;

  @Input() receivesCommentsIfRep = false;

  @Input() canChangeReceivers = true;

  @Input() maxContactPersons = 60;

  @Input() customerName: string;

  @Input() minCount = 1;

  formControls;

  constructor(
    private fb: UntypedFormBuilder,
    public configService: ConfigService,
    public contactValidationService: ContactValidationService,
  ) {
    super();
  }

  // Getter for contact persons form array
  get contactPersonsFormArray(): UntypedFormArray {
    return this.formControls?.['contactPersons'] as UntypedFormArray;
  }

  // Delegate to service for all validation-related methods
  get phoneNumberPlaceholder(): string {
    return this.contactValidationService.phoneNumberPlaceholder;
  }

  get phoneNumberErrorMessage(): string {
    return this.contactValidationService.phoneNumberErrorMessage;
  }

  get emailPlaceholder(): string {
    return this.contactValidationService.emailPlaceholder;
  }

  get emailErrorMessage(): string {
    return this.contactValidationService.emailErrorMessage;
  }

  get numberLabel(): string {
    return this.contactValidationService.numberLabel;
  }

  get emailLabel(): string {
    return this.contactValidationService.emailLabel;
  }

  get emailInputType(): string {
    return this.contactValidationService.emailInputType;
  }

  get textInputType(): string {
    return this.contactValidationService.textInputType;
  }

  getContactLabel(contactType: string): string {
    return this.contactValidationService.getContactLabel(contactType);
  }

  getContactPlaceholder(contactType: string): string {
    return this.contactValidationService.getContactPlaceholder(contactType);
  }

  getContactInputType(contactType: string): string {
    return this.contactValidationService.getContactInputType(contactType);
  }

  getContactErrorMessage(contactType: string): string {
    return this.contactValidationService.getContactErrorMessage(contactType);
  }

  getContactFieldProperties(contactType: string) {
    return this.contactValidationService.getContactFieldProperties(contactType);
  }

  getDynamicErrorMessage(contact: UntypedFormGroup): string {
    return this.contactValidationService.getDynamicErrorMessage(contact);
  }

  hasInvalidPhoneCharacters(value: string): boolean {
    return this.contactValidationService.hasInvalidPhoneCharacters(value);
  }

  hasInvalidPhoneCharactersForContact(contact: UntypedFormGroup): boolean {
    return this.contactValidationService.hasInvalidPhoneCharactersForContact(contact);
  }

  onKeyDown(event: KeyboardEvent, contactType: ContactType): void {
    this.contactValidationService.onKeyDown(event, contactType);
  }

  onPaste(event: ClipboardEvent, contact: UntypedFormGroup): void {
    this.contactValidationService.onPaste(event, contact);
  }

  onInput(event: Event, contact: UntypedFormGroup): void {
    this.contactValidationService.onInput(event, contact);
  }

  ngOnInit(): void {
    // Clear original values map to ensure clean state
    this.contactValidationService.clearOriginalValues();

    this.form.removeControl('contactPersons');
    const initialContactPerson = this.requiredOne ? [this.generateEmptyMainContactPerson()] : [];
    const contactPersons = this.contactPersons.length
      ? this.generateContactPersonsFormArray(this.contactPersons)
      : initialContactPerson;
    this.formControls = {
      contactPersons: this.fb.array(contactPersons),
    };
    this.buildForm(this.form, this.formControls, this.group);
    this.defineGetters(this.formControls);
    this.recheckIsMainSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // todo refactor this workaround
    if (changes.contactPersons && changes.contactPersons.currentValue.length) {
      this.ngOnInit();
    }
    if (changes.customerName) {
      this.setCustomerName(changes.customerName.currentValue);
    }
  }

  setCustomerName(customerName: string) {
    const persons = this.form.get('contactPersons') as UntypedFormArray;
    persons.controls.forEach((cp: UntypedFormGroup) => {
      if (cp.get('isMain').value) {
        cp.get('name').setValue(customerName);
      }
    });
  }

  getContactPersonsFormArray(): UntypedFormArray {
    return this.formControls['contactPersons'] as UntypedFormArray;
  }

  onAddContactPerson() {
    const contactPersons: UntypedFormArray = this.formControls[
      'contactPersons'
    ] as UntypedFormArray;
    contactPersons.push(this.generateContactPerson());
    this.recheckIsMainSelection();
  }

  onRemoveContactPerson(contactPersonIndex) {
    const contactPersons: UntypedFormArray = this.formControls[
      'contactPersons'
    ] as UntypedFormArray;
    const contactPerson: UntypedFormGroup = contactPersons.at(
      contactPersonIndex,
    ) as UntypedFormGroup;

    // Clean up original values for all contacts in the removed contact person
    const contacts: UntypedFormArray = contactPerson.controls['contacts'] as UntypedFormArray;
    contacts.controls.forEach((contact: UntypedFormGroup) => {
      const contactId = contact.controls.id.value;
      const contactType = contact.controls.type.value;
      this.contactValidationService.removeOriginalValue(contactId, contactType);
    });

    contactPersons.removeAt(contactPersonIndex);
    this.recheckIsMainSelection();
  }

  onRemoveContact(contactPersonIndex, contactIndex) {
    const contactPersons: UntypedFormArray = this.formControls[
      'contactPersons'
    ] as UntypedFormArray;
    const contactPerson: UntypedFormGroup = contactPersons.at(
      contactPersonIndex,
    ) as UntypedFormGroup;
    const contacts: UntypedFormArray = contactPerson.controls['contacts'] as UntypedFormArray;
    const contact = contacts.at(contactIndex) as UntypedFormGroup;

    // Clean up original values for the removed contact
    const contactId = contact.controls.id.value;
    const contactType = contact.controls.type.value;
    this.contactValidationService.removeOriginalValue(contactId, contactType);

    contacts.removeAt(contactIndex);
  }

  onAddContact(contactPersonIndex) {
    const contactPersons: UntypedFormArray = this.formControls[
      'contactPersons'
    ] as UntypedFormArray;
    const contactPerson: UntypedFormGroup = contactPersons.at(
      contactPersonIndex,
    ) as UntypedFormGroup;
    const contacts: UntypedFormArray = contactPerson.controls['contacts'] as UntypedFormArray;
    contacts.push(this.generateContactControl(ContactType.EMAIL, '', null));
  }

  onMainContactPersonChange(index: number) {
    const contactPersonsFormArray = this.currentFormGroup.get('contactPersons') as UntypedFormArray;
    contactPersonsFormArray.controls.forEach((contactPerson, contactPersonIndex) => {
      contactPerson.get('isMain').setValue(contactPersonIndex === index, {emitEvent: false});
    });
  }

  private recheckIsMainSelection() {
    const contactPersonsFormArray = this.currentFormGroup.get('contactPersons') as UntypedFormArray;

    if (contactPersonsFormArray.length === 1) {
      contactPersonsFormArray.at(0).get('isMain').setValue(true, {emitEvent: false});
    }
  }

  private generateContactsFormArray(contacts = []) {
    if (contacts.length === 0) {
      contacts = [{type: ContactType.EMAIL, value: ''} as Contact];
    }
    return this.fb.array(
      contacts.map((contact: Contact) => {
        return this.generateContactControl(contact.type, contact.value, contact.id);
      }),
    );
  }

  private generateContactControl(type = null, value = '', id?) {
    const controlKey = `${id || 'new'}_${type}`;

    const validators = this.contactValidationService.getValidatorsForContactType(type);

    const control = this.fb.group({
      id: this.fb.control(id),
      type: this.fb.control(type, Validators.required),
      value: this.fb.control(value, validators),
    });

    // Store original value for change detection
    this.contactValidationService.storeOriginalValue(id, type, value);

    // Set up value change detection
    this.contactValidationService.setupValueChangeDetection(control);

    return control;
  }

  private generateContactPersonsFormArray(contactPersons) {
    if (!contactPersons || !contactPersons.length) {
      return [];
    }
    return contactPersons.map((contactPerson: ContactPerson) => {
      const contacts = contactPerson.contacts;
      return this.generateContactPerson({
        id: contactPerson.id,
        name: contactPerson.name,
        notes: contactPerson.notes,
        notificationReceiver: contactPerson.notificationReceiver,
        invoiceReceiver: contactPerson.invoiceReceiver,
        pdfReceiver: contactPerson.pdfReceiver,
        receiveNotificationsRequestedRep: contactPerson.receiveNotificationsRequestedRep,
        receiveInvoiceRequestedRep: contactPerson.receiveInvoiceRequestedRep,
        receivesComments: contactPerson.receivesComments,
        receivesCommentsIfRep: contactPerson.receivesCommentsIfRep,
        contacts,
      });
    });
  }

  private generateContactPerson({
    id = null,
    name = null,
    notes = null,
    contacts = [],
    isMain = null,
    notificationReceiver = this.notificationReceiver,
    invoiceReceiver = this.invoiceReceiver,
    pdfReceiver = this.pdfReceiver,
    receiveNotificationsRequestedRep = this.receiveNotificationsRequestedRep,
    receiveInvoiceRequestedRep = this.receiveInvoiceRequestedRep,
    receivesComments = this.receivesComments,
    receivesCommentsIfRep = this.receivesCommentsIfRep,
  } = {}) {
    const isMainContactPerson = isMain === null ? this.mainContactPerson?.id === id : isMain;
    return this.fb.group({
      id: this.fb.control(id),
      name: this.fb.control(name, [Validators.required]),
      notes: this.fb.control(notes),
      contacts: this.generateContactsFormArray(contacts),
      isMain: this.fb.control(isMainContactPerson),
      notificationReceiver: this.fb.control(notificationReceiver),
      invoiceReceiver: this.fb.control(invoiceReceiver),
      pdfReceiver: this.fb.control(pdfReceiver),
      receiveNotificationsRequestedRep: this.fb.control(receiveNotificationsRequestedRep),
      receiveInvoiceRequestedRep: this.fb.control(receiveInvoiceRequestedRep),
      receivesComments: this.fb.control(receivesComments),
      receivesCommentsIfRep: this.fb.control(receivesCommentsIfRep),
    });
  }

  private generateEmptyMainContactPerson() {
    return this.generateContactPerson({
      isMain: true,
      notificationReceiver: this.receivedByDefaultEnabled,
      pdfReceiver: this.receivedByDefaultEnabled,
      invoiceReceiver: this.receivedByDefaultEnabled,
      receiveNotificationsRequestedRep: this.receiveNotificationsRequestedRep,
      receiveInvoiceRequestedRep: this.receiveInvoiceRequestedRep,
      receivesComments: this.receivesComments,
      receivesCommentsIfRep: this.receivesCommentsIfRep,
    });
  }

  /**
   * Changes the contact type and updates validation rules
   * @param event The type change event
   * @param contactPersonIndex The index of the contact person
   * @param contactIndex The index of the contact
   */
  changeContactType(event: any, contactPersonIndex: number, contactIndex: number): void {
    const contactPersonsFormArray: UntypedFormArray = this.formControls[
      'contactPersons'
    ] as UntypedFormArray;
    const contactPerson: UntypedFormGroup = contactPersonsFormArray.at(
      contactPersonIndex,
    ) as UntypedFormGroup;
    const contacts: UntypedFormArray = contactPerson.get('contacts') as UntypedFormArray;
    const contact = contacts.at(contactIndex) as UntypedFormGroup;

    this.contactValidationService.updateContactType(contact, event.value);
  }
}
